import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import '../css/serversPage.css';

const PLAYER_RANGES = ['24h', '7d', '30d', 'all'];
const HISTORY_RANGES = ['24h', '7d', '30d'];

const MODE_LABELS = { any: 'Any', solo: 'Solo', duo: 'Duo', trio: 'Trio', squad: 'Squad' };
const VIEW_LABELS = { any: 'Any', tpp: 'TPP', fpp: 'FPP' };
const CONTINENT_LABELS = {
    africa: 'Africa',
    asia: 'Asia',
    europe: 'Europe',
    northAmerica: 'North America',
    southAmerica: 'South America',
    oceania: 'Oceania',
    antarctica: 'Antarctica',
};

const mean = (values) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : null);

const timeFormat = (range) => (range === '24h' ? 'HH:mm' : 'MMM d');

const apiUrl = (path) => `${process.env.REACT_APP_API_URL}${path}`;

const ServersPage = () => {
    const { t, i18n } = useTranslation();

    const [list, setList] = useState(null);
    const [continent, setContinent] = useState('all');

    const [playerRange, setPlayerRange] = useState('24h');
    const [players, setPlayers] = useState({});

    const [historyRange, setHistoryRange] = useState('24h');
    const [history, setHistory] = useState({});
    const [selectedServer, setSelectedServer] = useState(null);

    const regionNames = useMemo(() => {
        try {
            return new Intl.DisplayNames([i18n.language], { type: 'region' });
        } catch {
            return null;
        }
    }, [i18n.language]);

    // Server list, refreshed every 30 s.
    useEffect(() => {
        let live = true;
        const load = async () => {
            try {
                const res = await fetch(apiUrl('/api/servers'), { headers: { 'Content-Type': 'application/json' } });
                if (!res.ok) return;
                const data = await res.json();
                if (live) setList(data);
            } catch {
                // keep the last list
            }
        };
        load();
        const timer = setInterval(load, 30000);
        return () => {
            live = false;
            clearInterval(timer);
        };
    }, []);

    // Global players for the active range.
    useEffect(() => {
        let live = true;
        const load = async () => {
            try {
                const res = await fetch(apiUrl(`/api/servers/players?range=${playerRange}`), {
                    headers: { 'Content-Type': 'application/json' },
                });
                if (!res.ok) return;
                const data = await res.json();
                if (live) setPlayers((old) => ({ ...old, [playerRange]: data }));
            } catch {
                // keep what is shown
            }
        };
        load();
        const timer = setInterval(load, 60000);
        return () => {
            live = false;
            clearInterval(timer);
        };
    }, [playerRange]);

    // Per-server history for the active range.
    useEffect(() => {
        let live = true;
        const load = async () => {
            try {
                const res = await fetch(apiUrl(`/api/servers/history?range=${historyRange}`), {
                    headers: { 'Content-Type': 'application/json' },
                });
                if (!res.ok) return;
                const data = await res.json();
                if (live) setHistory((old) => ({ ...old, [historyRange]: data }));
            } catch {
                // keep what is shown
            }
        };
        load();
        const timer = setInterval(load, 60000);
        return () => {
            live = false;
            clearInterval(timer);
        };
    }, [historyRange]);

    const servers = useMemo(() => list?.servers ?? [], [list]);
    const onlineCount = servers.filter((s) => s.online).length;
    const continents = useMemo(() => [...new Set(servers.map((s) => s.continent).filter(Boolean))], [servers]);
    const showRegionFilter = servers.length >= 6 && continents.length > 1;
    const shownServers =
        continent === 'all' ? servers : servers.filter((s) => s.continent === continent);

    const countryName = (code) => (regionNames && code ? (() => { try { return regionNames.of(code); } catch { return code; } })() : code);

    const updatedText = () => {
        if (!list?.updated) return '';
        const ago = Math.max(0, Math.floor(Date.now() / 1000) - list.updated);
        const time = ago < 60 ? t('servers.updatedSeconds', { seconds: ago }) : t('servers.updatedMinutes', { minutes: Math.floor(ago / 60) });
        return time;
    };

    // ---- Players section ----
    const playersView = players[playerRange];
    const playerPoints = useMemo(() => playersView?.points ?? [], [playersView]);
    const playerStats = useMemo(() => {
        if (!playerPoints.length) return null;
        const peak = Math.max(...playerPoints.map((p) => p.peak ?? 0));
        const average = mean(playerPoints.map((p) => p.online ?? 0));
        const inMatch = mean(playerPoints.map((p) => p.match ?? 0));
        const different = playersView.range === 'all' ? playersView.unique?.all : playersView.unique?.[playersView.range];
        return { peak, average, inMatch, different };
    }, [playerPoints, playersView]);

    const playerChartData = useMemo(
        () =>
            playerPoints.map((p) => ({
                time: format(new Date(p.t * 1000), timeFormat(playerRange)),
                online: p.peak ?? 0,
                match: p.match ?? 0,
            })),
        [playerPoints, playerRange],
    );

    // ---- History section ----
    const historyView = history[historyRange];
    const historyServers = useMemo(() => historyView?.servers ?? [], [historyView]);
    const activeServer =
        selectedServer && historyServers.some((s) => s.name === selectedServer)
            ? selectedServer
            : historyServers.find((s) => s.points?.length)?.name ?? historyServers[0]?.name ?? null;

    const historyPoints = useMemo(
        () => historyServers.find((s) => s.name === activeServer)?.points ?? [],
        [historyServers, activeServer],
    );
    const historyStats = useMemo(() => {
        if (!historyPoints.length) return null;
        const peaks = historyPoints.map((p) => p.peak).filter((v) => v !== null);
        const pls = historyPoints.map((p) => p.players).filter((v) => v !== null);
        const pings = historyPoints.map((p) => p.ping).filter((v) => v !== null);
        const ups = historyPoints.map((p) => p.up);
        return {
            peak: peaks.length ? Math.max(...peaks) : null,
            average: mean(pls),
            uptime: mean(ups),
            avgPing: mean(pings),
        };
    }, [historyPoints]);

    const historyChartData = useMemo(
        () =>
            historyPoints.map((p) => ({
                time: format(new Date(p.t * 1000), timeFormat(historyRange)),
                players: p.peak,
                ping: p.ping,
            })),
        [historyPoints, historyRange],
    );

    const loadingList = list === null;
    const loadingPlayers = !playersView;
    const loadingHistory = !historyView;

    const number = (v) => {
        if (v === null || v === undefined) return '—';
        return new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(v);
    };
    const percent = (v) => {
        if (v === null || v === undefined) return '—';
        return `${Math.round(v * 1000) / 10}%`;
    };

    const chartTooltip = (nameKey) => ({
        contentStyle: {
            background: '#14141c',
            border: '1px solid rgba(255,255,255,0.14)',
            borderRadius: 0,
            color: '#f5f5f7',
        },
        labelStyle: { color: '#a1a1aa' },
        formatter: (value) => [number(value), t(nameKey)],
    });

    return (
        <div className="servers-page">
            <section className="servers-hero">
                <div className="sp-container">
                    <div className="sp-section-head servers-head">
                        <span className="sp-eyebrow">{t('servers.eyebrow')}</span>
                        <h1 className="sp-title">{t('servers.title')}</h1>
                        <div className="line-wrapper">
                            <img src="/images/sp-line.png" alt="Sp_line" className="line" />
                        </div>
                        <p className="servers-subtitle">{t('servers.subtitle')}</p>
                    </div>
                </div>
            </section>

            <section className="servers-main">
                <div className="sp-container">
                    {/* Server list */}
                    <div className="servers-summary">
                        <span className={`servers-pulse ${onlineCount > 0 ? 'is-on' : ''}`} />
                        {loadingList ? (
                            <span className="skeleton-line skeleton-summary" />
                        ) : (
                            <span>
                                {onlineCount > 0 && onlineCount === servers.length
                                    ? t('servers.allOnline')
                                    : t('servers.onlineSummary', { online: onlineCount, total: servers.length })}
                            </span>
                        )}
                        {list?.updated && <span className="servers-updated">{updatedText()}</span>}
                    </div>

                    {showRegionFilter && (
                        <div className="servers-filter">
                            {['all', ...continents].map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    className={`servers-filter-btn ${continent === c ? 'is-active' : ''}`}
                                    onClick={() => setContinent(c)}
                                >
                                    {c === 'all' ? t('servers.allRegions') : CONTINENT_LABELS[c]}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="server-grid">
                        {loadingList
                            ? [0, 1, 2, 3].map((i) => (
                                  <div key={i} className="server-card servers-skeleton">
                                      <div className="skeleton-line skeleton-name" />
                                      <div className="skeleton-line skeleton-where" />
                                      <div className="skeleton-line skeleton-tags" />
                                      <div className="skeleton-line skeleton-live" />
                                  </div>
                              ))
                            : shownServers.map((srv) => (
                            <div
                                key={srv.name}
                                className={`server-card ${srv.online ? 'is-online' : ''}`}
                            >
                                <div className="server-card-top">
                                    <h3 className="server-card-name">
                                        <span className="server-card-dot" />
                                        {srv.name}
                                    </h3>
                                    <span className="server-card-status">
                                        {srv.online ? t('servers.online') : t('servers.offline')}
                                    </span>
                                </div>
                                <p className="server-card-where">
                                    {srv.continent && CONTINENT_LABELS[srv.continent]}
                                    {srv.country ? ` · ${countryName(srv.country)}` : ''}
                                </p>
                                <div className="server-card-tags">
                                    <span className="server-card-tag">{MODE_LABELS[srv.mode] ?? 'Any'}</span>
                                    <span className="server-card-tag">{VIEW_LABELS[srv.view] ?? 'Any'}</span>
                                </div>
                                <p className="server-card-live">
                                    {srv.match && t(`servers.match_${srv.match}`)}
                                    {srv.players !== null && (
                                        <>
                                            {' · '}
                                            {srv.players === 1 ? t('servers.onePlayer') : t('servers.players', { count: srv.players })}
                                        </>
                                    )}
                                    {srv.ping !== null && ` · ${t('servers.ping', { ms: srv.ping })}`}
                                </p>
                            </div>
                            ))}
                    </div>

                    {/* Players */}
                    <section className="servers-block">
                        <div className="servers-block-head">
                            <div>
                                <h2 className="servers-block-title">{t('servers.playersTitle')}</h2>
                                <p className="servers-block-lead">{t('servers.playersLead')}</p>
                            </div>
                            <div className="servers-range">
                                {PLAYER_RANGES.map((r) => (
                                    <button
                                        key={r}
                                        type="button"
                                        className={`servers-range-btn ${playerRange === r ? 'is-active' : ''}`}
                                        onClick={() => setPlayerRange(r)}
                                    >
                                        {r.toUpperCase()}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {loadingPlayers ? (
                            <div className="servers-stats">
                                {[0, 1, 2, 3].map((i) => (
                                    <div key={i} className="servers-stat servers-skeleton">
                                        <div className="skeleton-line skeleton-label" />
                                        <div className="skeleton-line skeleton-value" />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            playerStats && (
                                <div className="servers-stats">
                                    <div className="servers-stat">
                                        <span className="servers-stat-label">{t('servers.peak')}</span>
                                        <span className="servers-stat-value">{number(playerStats.peak)}</span>
                                    </div>
                                    <div className="servers-stat">
                                        <span className="servers-stat-label">{t('servers.average')}</span>
                                        <span className="servers-stat-value">{number(playerStats.average)}</span>
                                    </div>
                                    <div className="servers-stat">
                                        <span className="servers-stat-label">{t('servers.matchAverage')}</span>
                                        <span className="servers-stat-value">{number(playerStats.inMatch)}</span>
                                    </div>
                                    <div className="servers-stat">
                                        <span className="servers-stat-label">
                                            {playerRange === 'all' ? t('servers.allPlayers') : t('servers.uniquePlayers')}
                                        </span>
                                        <span className="servers-stat-value">{number(playerStats.different)}</span>
                                    </div>
                                </div>
                            )
                        )}

                        {loadingPlayers ? (
                            <div className="servers-charts">
                                <div className="servers-chart servers-skeleton">
                                    <div className="skeleton-line skeleton-chart" />
                                </div>
                                <div className="servers-chart servers-skeleton">
                                    <div className="skeleton-line skeleton-chart" />
                                </div>
                            </div>
                        ) : (
                            playerChartData.length > 0 && (
                                <div className="servers-charts">
                                    <div className="servers-chart">
                                        <h3 className="servers-chart-title">{t('servers.onlineChart')}</h3>
                                        <ResponsiveContainer width="100%" height={200}>
                                            <AreaChart data={playerChartData} margin={{ top: 10, right: 10, left: -16, bottom: 0 }}>
                                                <defs>
                                                    <linearGradient id="spOnline" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="0%" stopColor="#ff0048" stopOpacity={0.3} />
                                                        <stop offset="100%" stopColor="#ff0048" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                                                <XAxis dataKey="time" tick={{ fill: '#71717a', fontSize: 11 }} minTickGap={48} axisLine={false} tickLine={false} />
                                                <YAxis tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} width={36} />
                                                <Tooltip {...chartTooltip('servers.onlineChart')} />
                                                <Area type="monotone" dataKey="online" stroke="#ff0048" strokeWidth={2} fill="url(#spOnline)" isAnimationActive={false} />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className="servers-chart">
                                        <h3 className="servers-chart-title">{t('servers.matchChart')}</h3>
                                        <ResponsiveContainer width="100%" height={200}>
                                            <AreaChart data={playerChartData} margin={{ top: 10, right: 10, left: -16, bottom: 0 }}>
                                                <defs>
                                                    <linearGradient id="spMatch" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="0%" stopColor="#ff3d6e" stopOpacity={0.3} />
                                                        <stop offset="100%" stopColor="#ff3d6e" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                                                <XAxis dataKey="time" tick={{ fill: '#71717a', fontSize: 11 }} minTickGap={48} axisLine={false} tickLine={false} />
                                                <YAxis tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} width={36} />
                                                <Tooltip {...chartTooltip('servers.matchChart')} />
                                                <Area type="monotone" dataKey="match" stroke="#ff6a8d" strokeWidth={2} fill="url(#spMatch)" isAnimationActive={false} />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            )
                        )}
                    </section>

                    {/* History */}
                    <section className="servers-block">
                        <div className="servers-block-head">
                            <div>
                                <h2 className="servers-block-title">{t('servers.history')}</h2>
                                <p className="servers-block-lead">{t('servers.historyLead')}</p>
                            </div>
                            <div className="servers-block-controls">
                                {historyServers.length > 1 && (
                                    <select
                                        className="servers-select"
                                        value={activeServer ?? ''}
                                        onChange={(e) => setSelectedServer(e.target.value)}
                                        aria-label={t('servers.historyServer')}
                                    >
                                        {historyServers.map((s) => (
                                            <option key={s.name} value={s.name}>
                                                {s.name}
                                            </option>
                                        ))}
                                    </select>
                                )}
                                <div className="servers-range">
                                    {HISTORY_RANGES.map((r) => (
                                        <button
                                            key={r}
                                            type="button"
                                            className={`servers-range-btn ${historyRange === r ? 'is-active' : ''}`}
                                            onClick={() => setHistoryRange(r)}
                                        >
                                            {r.toUpperCase()}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {loadingHistory ? (
                            <div className="servers-stats">
                                {[0, 1, 2, 3].map((i) => (
                                    <div key={i} className="servers-stat servers-skeleton">
                                        <div className="skeleton-line skeleton-label" />
                                        <div className="skeleton-line skeleton-value" />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            historyStats && (
                                <div className="servers-stats">
                                    <div className="servers-stat">
                                        <span className="servers-stat-label">{t('servers.peak')}</span>
                                        <span className="servers-stat-value">{number(historyStats.peak)}</span>
                                    </div>
                                    <div className="servers-stat">
                                        <span className="servers-stat-label">{t('servers.average')}</span>
                                        <span className="servers-stat-value">{number(historyStats.average)}</span>
                                    </div>
                                    <div className="servers-stat">
                                        <span className="servers-stat-label">{t('servers.uptime')}</span>
                                        <span className="servers-stat-value">{percent(historyStats.uptime)}</span>
                                    </div>
                                    <div className="servers-stat">
                                        <span className="servers-stat-label">{t('servers.avgPing')}</span>
                                        <span className="servers-stat-value">
                                            {historyStats.avgPing === null ? '—' : t('servers.ping', { ms: Math.round(historyStats.avgPing) })}
                                        </span>
                                    </div>
                                </div>
                            )
                        )}

                        {loadingHistory ? (
                            <div className="servers-charts">
                                <div className="servers-chart servers-skeleton">
                                    <div className="skeleton-line skeleton-chart" />
                                </div>
                                <div className="servers-chart servers-skeleton">
                                    <div className="skeleton-line skeleton-chart" />
                                </div>
                            </div>
                        ) : (
                            historyChartData.length > 0 && (
                                <div className="servers-charts">
                                    <div className="servers-chart">
                                        <h3 className="servers-chart-title">{t('servers.playersChart')}</h3>
                                        <ResponsiveContainer width="100%" height={200}>
                                            <AreaChart data={historyChartData} margin={{ top: 10, right: 10, left: -16, bottom: 0 }}>
                                                <defs>
                                                    <linearGradient id="spSrvPlayers" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="0%" stopColor="#ff0048" stopOpacity={0.3} />
                                                        <stop offset="100%" stopColor="#ff0048" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                                                <XAxis dataKey="time" tick={{ fill: '#71717a', fontSize: 11 }} minTickGap={48} axisLine={false} tickLine={false} />
                                                <YAxis tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} width={36} />
                                                <Tooltip {...chartTooltip('servers.playersChart')} />
                                                <Area type="monotone" dataKey="players" stroke="#ff0048" strokeWidth={2} fill="url(#spSrvPlayers)" connectNulls={false} isAnimationActive={false} />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className="servers-chart">
                                        <h3 className="servers-chart-title">{t('servers.pingChart')}</h3>
                                        <ResponsiveContainer width="100%" height={200}>
                                            <AreaChart data={historyChartData} margin={{ top: 10, right: 10, left: -16, bottom: 0 }}>
                                                <defs>
                                                    <linearGradient id="spSrvPing" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="0%" stopColor="#22c55e" stopOpacity={0.3} />
                                                        <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                                                <XAxis dataKey="time" tick={{ fill: '#71717a', fontSize: 11 }} minTickGap={48} axisLine={false} tickLine={false} />
                                                <YAxis tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} width={36} />
                                                <Tooltip {...chartTooltip('servers.pingChart')} />
                                                <Area type="monotone" dataKey="ping" stroke="#22c55e" strokeWidth={2} fill="url(#spSrvPing)" connectNulls={false} isAnimationActive={false} />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            )
                        )}
                    </section>
                </div>
            </section>
        </div>
    );
};

export default ServersPage;
